import { Box } from "@mui/material";
import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";

import { Loading } from "components";

import {
  PDF_DOCUMENT_DEFAULT_HEIGHT,
  PDF_DOCUMENT_DEFAULT_WIDTH,
} from "../consts";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "../css/style.css";
import { PdfDocumentProps } from "./types";

const ACTIVE_BBOX_EFFECT = "bbox-active-pulse";

const LoadingPDF = () => (
  <Box
    sx={{
      width: PDF_DOCUMENT_DEFAULT_WIDTH,
      height: PDF_DOCUMENT_DEFAULT_HEIGHT,
      position: "relative",
    }}
  >
    <Loading />
  </Box>
);

export const PdfDocument: FunctionComponent<PdfDocumentProps> = ({
  scale,
  file,
  onLoadSuccess,
  width,
  bboxes = {},
  dimensions = [],
  pageNumber,
  scrollVersion,
  activeBboxKey,
}) => {
  const [renderedWidth, setRenderedWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const firstPageRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Reset page count when the file changes so stale Page components unmount
  // before PDF.js destroys the old worker, preventing sendWithPromise errors.
  useEffect(() => {
    setNumPages(0);
    setRenderedWidth(0);
  }, [file]);

  // Scroll to the target page. Depends on scrollVersion so it re-fires even
  // when the page number hasn't changed (e.g. user clicked the same page twice).
  useEffect(() => {
    if (!pageNumber || numPages === 0) return;
    pageRefs.current
      .get(pageNumber)
      ?.scrollIntoView({ behavior: "instant" as ScrollBehavior });
  }, [pageNumber, numPages, scrollVersion]);

  const handleDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    onLoadSuccess?.({ numPages: n });
  };

  const handleFirstPageRenderSuccess = () => {
    const canvas = firstPageRef.current?.querySelector("canvas");
    if (canvas) {
      setRenderedWidth(canvas.offsetWidth);
    }
  };

  return (
    <Box>
      <Document
        file={file}
        onLoadSuccess={handleDocumentLoadSuccess}
        loading={<LoadingPDF />}
        error={null}
      >
        {Array.from({ length: numPages }, (_, i) => {
          const pageDim = dimensions[i];
          const pageScaleX =
            renderedWidth > 0 && pageDim ? renderedWidth / pageDim.Width : 0;

          return (
            <div
              key={i}
              data-testid={i === 0 ? "pdf-page-wrapper" : undefined}
              ref={(el) => {
                if (i === 0) firstPageRef.current = el;
                if (el) pageRefs.current.set(i + 1, el);
              }}
              style={{
                position: "relative",
                display: "block",
                marginBottom: i < numPages - 1 ? 8 : 0,
              }}
            >
              <Page
                pageNumber={i + 1}
                width={width * (scale / 0.75)}
                onRenderSuccess={
                  i === 0 ? handleFirstPageRenderSuccess : undefined
                }
                onRenderError={() => {}}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={<LoadingPDF />}
              />
              {pageScaleX > 0 &&
                Object.entries(bboxes)
                  .filter(([, item]) => item.page === i)
                  .map(([bboxKey, bbox]) => {
                    const isActive = bboxKey === activeBboxKey;
                    return (
                      <div
                        // Re-keying on scrollVersion forces a remount when the
                        // same field is clicked again, restarting the animation.
                        key={
                          isActive
                            ? `active-${scrollVersion}`
                            : `${bbox.tlh}-${bbox.tlv}-${bbox.brh}-${bbox.brv}`
                        }
                        data-testid="bbox-overlay"
                        className={isActive ? ACTIVE_BBOX_EFFECT : undefined}
                        style={{
                          position: "absolute",
                          left: bbox.tlh * pageScaleX,
                          top: bbox.tlv * pageScaleX,
                          width: (bbox.brh - bbox.tlh) * pageScaleX,
                          height: (bbox.brv - bbox.tlv) * pageScaleX,
                          background: "rgba(255, 230, 0, 0.4)",
                          pointerEvents: "none",
                        }}
                      />
                    );
                  })}
            </div>
          );
        })}
      </Document>
    </Box>
  );
};
